// GraphQL の pageKeys は String で返る（Rails 側のページ構成変更で値が古くなっても
// クエリ全体が壊れないようにするため）。そのためページキーの正しさは
// この定数がタイポなく Rails 側 Announcement::PAGE_KEY_GROUPS（ryde-pass リポジトリ
// app/models/announcement.rb）と一致していることに懸かっている。
//
// キーは「ドメインのキー」+「配下ページの接尾辞」で構成される（詳細は Rails 側コメント参照）。
// 例: special_ticket ドメイン配下の trips ページ → special_ticket_trips
//
// 新規ページでお知らせ表示に対応させる手順:
//   1. Rails 側 PAGE_KEY_GROUPS にドメイン or 配下ページの接尾辞を追加
//   2. このファイルの ANNOUNCEMENT_PAGE_KEY_VALUES に完全なキーを追加
//   3. このファイルの ANNOUNCEMENT_PAGE_KEYS に pathname とキー配列の対応を追加
// いずれか一つでも漏れると、そのページでお知らせが表示されない（または管理画面で選択できない）
const ANNOUNCEMENT_PAGE_KEY_VALUES = [
  'top',
  'account',
  'push_notification',
  'special_ticket',
  'special_ticket_sales_summary',
  'special_ticket_start_usage_details',
  'special_ticket_products',
  'special_ticket_product_edit',
  'special_ticket_trips',
  'special_ticket_trip_edit',
  'special_ticket_generate_qr',
  'simple_pass',
  'simple_pass_my_passes',
  'simple_pass_my_pass_detail',
  'simple_pass_sales_summary',
  'simple_pass_purchase_details',
  'multi_ticket',
  'multi_ticket_user_items',
  'multi_ticket_user_item_detail',
  'multi_ticket_sales_summary',
  'multi_ticket_purchase_details',
  'analytics',
  'analytics_product_details',
  'analytics_product_cross_sell',
  'analytics_ticket_usages',
  'analytics_product_reviews',
  'analytics_ticket_reviews',
  'analytics_simple_pass_user_demographics',
  'analytics_multi_ticket_user_demographics',
] as const

export type AnnouncementPageKey = (typeof ANNOUNCEMENT_PAGE_KEY_VALUES)[number]

// お知らせバナーの表示対象ページ一覧
// ページ追加時はここに pathname (router.pathname と同じ表記) をキーとして追加する
const ANNOUNCEMENT_PAGE_KEYS = {
  '/': ['top'],
  '/account_settings': ['account'],
  '/analytics/multi_ticket_user_demographics': [
    'analytics',
    'analytics_multi_ticket_user_demographics',
  ],
  '/analytics/product_cross_sell': [
    'analytics',
    'analytics_product_cross_sell',
  ],
  '/analytics/product_details': ['analytics', 'analytics_product_details'],
  '/analytics/product_reviews': ['analytics', 'analytics_product_reviews'],
  '/analytics/simple_pass_user_demographics': [
    'analytics',
    'analytics_simple_pass_user_demographics',
  ],
  '/analytics/ticket_reviews': ['analytics', 'analytics_ticket_reviews'],
  '/analytics/ticket_usages': ['analytics', 'analytics_ticket_usages'],
  '/multi_ticket/purchase_details': [
    'multi_ticket',
    'multi_ticket_purchase_details',
  ],
  '/multi_ticket/sales_summary': ['multi_ticket', 'multi_ticket_sales_summary'],
  '/multi_ticket/user_items': ['multi_ticket', 'multi_ticket_user_items'],
  '/multi_ticket/user_items/[id]/detail': [
    'multi_ticket',
    'multi_ticket_user_item_detail',
  ],
  '/push_notifications': ['push_notification'],
  '/simple_pass/my_passes': ['simple_pass', 'simple_pass_my_passes'],
  '/simple_pass/my_passes/[id]/detail': [
    'simple_pass',
    'simple_pass_my_pass_detail',
  ],
  '/simple_pass/purchase_details': [
    'simple_pass',
    'simple_pass_purchase_details',
  ],
  '/simple_pass/sales_summary': ['simple_pass', 'simple_pass_sales_summary'],
  '/special_ticket/generate_qr': [
    'special_ticket',
    'special_ticket_generate_qr',
  ],
  '/special_ticket/products': ['special_ticket', 'special_ticket_products'],
  '/special_ticket/products/[id]/edit': [
    'special_ticket',
    'special_ticket_product_edit',
  ],
  '/special_ticket/sales_summary': [
    'special_ticket',
    'special_ticket_sales_summary',
  ],
  '/special_ticket/start_usage_details': [
    'special_ticket',
    'special_ticket_start_usage_details',
  ],
  '/special_ticket/trips': ['special_ticket', 'special_ticket_trips'],
  '/special_ticket/trips/[id]/edit': [
    'special_ticket',
    'special_ticket_trip_edit',
  ],
  '/special_ticket/trips/edit': ['special_ticket'],
} as const satisfies Record<string, readonly AnnouncementPageKey[]>

export const getAnnouncementPageKeys = (
  pathname: string
): readonly AnnouncementPageKey[] =>
  ANNOUNCEMENT_PAGE_KEYS[pathname as keyof typeof ANNOUNCEMENT_PAGE_KEYS] ?? []
